import { getBrowserId } from '@linktr.ee/client-storage';
import { EventConstructorArgs } from '@linktr.ee/event-lib/envelope/Event';
import { AuthIdentifierSubmitted } from '@linktr.ee/event-lib/events/authIdentifierSubmitted';
import { AuthMfaChallengeRequestCompleted } from '@linktr.ee/event-lib/events/authMfaChallengeRequestCompleted';
import { AuthMfaChallengeRequested } from '@linktr.ee/event-lib/events/authMfaChallengeRequested';
import { AuthMfaVerificationFailed } from '@linktr.ee/event-lib/events/authMfaVerificationFailed';
import { AuthMfaVerificationSubmitted } from '@linktr.ee/event-lib/events/authMfaVerificationSubmitted';
import { AuthOtpChallengeRequestCompleted } from '@linktr.ee/event-lib/events/authOtpChallengeRequestCompleted';
import { AuthOtpChallengeRequested } from '@linktr.ee/event-lib/events/authOtpChallengeRequested';
import { AuthOtpVerificationFailed } from '@linktr.ee/event-lib/events/authOtpVerificationFailed';
import { AuthOtpVerificationSubmitted } from '@linktr.ee/event-lib/events/authOtpVerificationSubmitted';
import { AuthPasswordPromptDisplayed } from '@linktr.ee/event-lib/events/authPasswordPromptDisplayed';
import { AuthPasswordSubmitted } from '@linktr.ee/event-lib/events/authPasswordSubmitted';
import { AuthPasswordVerificationFailed } from '@linktr.ee/event-lib/events/authPasswordVerificationFailed';
import { AuthProtectedIdentifierVerificationCompleted } from '@linktr.ee/event-lib/events/authProtectedIdentifierVerificationCompleted';
import { AuthProtectedIdentifierVerificationSubmitted } from '@linktr.ee/event-lib/events/authProtectedIdentifierVerificationSubmitted';
import { ErrorEncountered } from '@linktr.ee/event-lib/events/errorEncountered';
import { GenericComponentInteracted } from '@linktr.ee/event-lib/events/genericComponentInteracted';
import { PageViewed } from '@linktr.ee/event-lib/events/pageViewed';
import { ScreenViewedGolden as ScreenViewed } from '@linktr.ee/event-lib/events/screenViewedGolden';
import { UserLoginAttempted } from '@linktr.ee/event-lib/events/userLoginAttempted';
import { UserLoginStarted } from '@linktr.ee/event-lib/events/userLoginStarted';
import { UserSignupStarted } from '@linktr.ee/event-lib/events/userSignupStarted';
import { publishEvent, publishNonGoldenEvent } from './client';
import { isMobileApp } from '../descopeQueryParams';

// TODO: revisit to see if we need the datadogRum and global.window.__LTAUTHMOBILEAPP__

export type AuthFlow = EventConstructorArgs<
  'AuthIdentifierSubmitted',
  '1-0-0'
>['payload']['authFlow'];
type AuthIdentifierType = EventConstructorArgs<
  'AuthIdentifierSubmitted',
  '1-0-0'
>['payload']['identifierType'];
export type AuthOtpRequestTrigger = EventConstructorArgs<
  'AuthOtpChallengeRequested',
  '1-0-0'
>['payload']['requestTrigger'];
export type AuthOtpChannel = EventConstructorArgs<
  'AuthOtpChallengeRequested',
  '1-0-0'
>['payload']['otpChannel'];
export type AuthMfaType = EventConstructorArgs<
  'AuthMfaChallengeRequested',
  '1-0-0'
>['payload']['mfaType'];
export type AuthMfaFlow = EventConstructorArgs<
  'AuthMfaChallengeRequested',
  '1-0-0'
>['payload']['mfaFlow'];
export type AuthMfaRequestTrigger = EventConstructorArgs<
  'AuthMfaChallengeRequested',
  '1-0-0'
>['payload']['requestTrigger'];
type AuthOtpChallengeRequestFailureReasonCode = NonNullable<
  EventConstructorArgs<
    'AuthOtpChallengeRequestCompleted',
    '2-0-0'
  >['payload']['failureReasonCode']
>;
type AuthOtpVerificationFailureReasonCode = EventConstructorArgs<
  'AuthOtpVerificationFailed',
  '1-0-0'
>['payload']['failureReasonCode'];
type AuthMfaChallengeRequestFailureReasonCode = NonNullable<
  EventConstructorArgs<
    'AuthMfaChallengeRequestCompleted',
    '1-0-0'
  >['payload']['failureReasonCode']
>;
type AuthMfaVerificationFailureReasonCode = EventConstructorArgs<
  'AuthMfaVerificationFailed',
  '1-0-0'
>['payload']['failureReasonCode'];
type AuthPasswordVerificationFailureReasonCode = EventConstructorArgs<
  'AuthPasswordVerificationFailed',
  '1-0-0'
>['payload']['failureReasonCode'];
export type AuthProtectedIdentifierVerificationFailureReasonCode = NonNullable<
  EventConstructorArgs<
    'AuthProtectedIdentifierVerificationCompleted',
    '1-0-0'
  >['payload']['failureReasonCode']
>;
type GenericComponentScreenTitle = EventConstructorArgs<
  'GenericComponentInteracted',
  '1-0-0'
>['payload']['screen']['screenTitle'];

const OTP_CHANNEL_EMAIL: AuthOtpChannel = 'email';

export function getAuthIdentifierType(identifier: string): AuthIdentifierType {
  const trimmedIdentifier = identifier.trim();

  if (!trimmedIdentifier) {
    return 'unknown';
  }

  return trimmedIdentifier.includes('@') ? 'email' : 'username';
}

const getErrorStringProperty = (
  error: unknown,
  property: string
): string | undefined => {
  if (!error || typeof error !== 'object' || !(property in error)) {
    return undefined;
  }

  const value = (error as Record<string, unknown>)[property];
  return typeof value === 'string' ? value : undefined;
};

const normaliseErrorText = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message.toLowerCase();
  }

  if (typeof error === 'string') {
    return error.toLowerCase();
  }

  const objectErrorText =
    getErrorStringProperty(error, 'text') ||
    getErrorStringProperty(error, 'message') ||
    getErrorStringProperty(error, 'description') ||
    getErrorStringProperty(error, 'errorDescription');

  if (objectErrorText) {
    return objectErrorText.toLowerCase();
  }

  return '';
};

export function getAuthOtpChallengeRequestFailureReasonCode(
  error: unknown
): AuthOtpChallengeRequestFailureReasonCode {
  const errorText = normaliseErrorText(error);

  if (errorText.includes('rate') || errorText.includes('too many')) {
    return 'rate_limited';
  }

  if (errorText.includes('recipient') || errorText.includes('email')) {
    return 'invalid_recipient';
  }

  if (errorText.includes('provider') || errorText.includes('delivery')) {
    return 'delivery_provider_error';
  }

  return 'unknown';
}

export function getAuthOtpVerificationFailureReasonCode(
  error: unknown
): AuthOtpVerificationFailureReasonCode {
  const errorText = normaliseErrorText(error);

  if (errorText.includes('expired')) {
    return 'expired_code';
  }

  if (
    errorText.includes('too many') ||
    errorText.includes('attempt') ||
    errorText.includes('rate')
  ) {
    return 'too_many_attempts';
  }

  if (
    errorText.includes('incorrect') ||
    errorText.includes('invalid') ||
    errorText.includes('wrong') ||
    errorText.includes('code')
  ) {
    return 'incorrect_code';
  }

  return 'unknown';
}

export function getAuthMfaChallengeRequestFailureReasonCode(
  error: unknown
): AuthMfaChallengeRequestFailureReasonCode {
  return getAuthOtpChallengeRequestFailureReasonCode(error);
}

export function getAuthMfaVerificationFailureReasonCode(
  error: unknown
): AuthMfaVerificationFailureReasonCode {
  return getAuthOtpVerificationFailureReasonCode(error);
}

export function getAuthMfaGenericInteractionScreenTitle(
  mfaFlow: AuthMfaFlow
): GenericComponentScreenTitle {
  return mfaFlow === 'login_challenge'
    ? 'Log In - Enter MFA Code'
    : 'Account Settings';
}

export function getAuthPasswordVerificationFailureReasonCode(
  error: unknown
): AuthPasswordVerificationFailureReasonCode {
  const errorText = normaliseErrorText(error);

  if (errorText.includes('rate') || errorText.includes('too many')) {
    return 'rate_limited';
  }

  if (
    errorText.includes('wrong password') ||
    errorText.includes('incorrect password') ||
    errorText.includes('invalid_password') ||
    errorText.includes('invalid password')
  ) {
    return 'incorrect_password';
  }

  if (
    errorText.includes('unknown user') ||
    errorText.includes('not found') ||
    errorText.includes('does not exist')
  ) {
    return 'unknown_user';
  }

  return 'unknown';
}

export function getAuthProtectedIdentifierVerificationFailureReasonCode(
  error: unknown
): AuthProtectedIdentifierVerificationFailureReasonCode {
  const errorText = normaliseErrorText(error);

  if (errorText.includes('rate') || errorText.includes('too many')) {
    return 'rate_limited' as AuthProtectedIdentifierVerificationFailureReasonCode;
  }

  if (errorText.includes('email') || errorText.includes('invalid')) {
    return 'invalid_email' as AuthProtectedIdentifierVerificationFailureReasonCode;
  }

  if (
    errorText.includes('confirm') ||
    errorText.includes('match') ||
    errorText.includes('verify')
  ) {
    return 'verification_failed' as AuthProtectedIdentifierVerificationFailureReasonCode;
  }

  return 'unknown' as AuthProtectedIdentifierVerificationFailureReasonCode;
}

export function createScreenViewedEvent(
  screenTitle: EventConstructorArgs<
    'ScreenViewedGolden',
    '14-13-0'
  >['payload']['screen']['screenTitle']
) {
  return new ScreenViewed({
    version: '14-13-0',
    payload: {
      screen: {
        productArea: 'Auth',
        screenTitle,
      },
    },
  });
}

export function publishScreenViewedEvent(
  screenTitle: EventConstructorArgs<
    'ScreenViewedGolden',
    '14-13-0'
  >['payload']['screen']['screenTitle']
) {
  publishEvent(createScreenViewedEvent(screenTitle));
}

export function publishSignupStartedEvent(
  signupMethod: EventConstructorArgs<
    'UserSignupStarted',
    '1-0-2'
  >['payload']['signupMethod']
) {
  const event = new UserSignupStarted({
    version: '1-0-2',
    payload: {
      signupMethod,
    },
  });
  publishEvent(event);
}

export function publishLoginStartedEvent(
  loginMethod: EventConstructorArgs<
    'UserLoginStarted',
    '1-0-1'
  >['payload']['loginMethod']
) {
  const event = new UserLoginStarted({
    version: '1-0-1',
    payload: {
      loginMethod,
    },
  });
  publishEvent(event);
}

export const publishLoginErrorEncounteredEvent = (
  loginMethod: string,
  errorMessage: string
) => {
  const event = new ErrorEncountered({
    version: '2-0-0',
    payload: {
      errorType: 'Login Error',
      errorDetail: `${loginMethod} - ${errorMessage}`,
      screen: {
        screenTitle: 'Log In',
      },
    },
  });
  publishEvent(event);
};

export const publishSignupErrorEncounteredEvent = (
  signupMethod: string,
  errorMessage: string
) => {
  const event = new ErrorEncountered({
    version: '2-0-0',
    payload: {
      errorType: 'Signup Error',
      errorDetail: `${signupMethod} - ${errorMessage}`,
      screen: {
        screenTitle: 'Sign Up',
      },
    },
  });
  publishEvent(event);
};

type ArkoseGateFailureSource = 'continue_error' | 'terminal_error';

const publishArkoseGateFailureEvent = (
  authFlow: 'login' | 'signup',
  failureSource: ArkoseGateFailureSource
) => {
  const event = new ErrorEncountered({
    version: '2-0-0',
    payload: {
      errorType: authFlow === 'login' ? 'Login Error' : 'Signup Error',
      errorDetail: `Arkose Browser Check - ${failureSource}`,
      screen: {
        screenTitle: authFlow === 'login' ? 'Log In' : 'Sign Up',
      },
    },
  });
  publishEvent(event);
};

export const publishArkoseGateContinueFailureEvent = (
  authFlow: 'login' | 'signup'
) => publishArkoseGateFailureEvent(authFlow, 'continue_error');

export const publishArkoseGateTerminalFailureEvent = (
  authFlow: 'login' | 'signup'
) => publishArkoseGateFailureEvent(authFlow, 'terminal_error');

export function publishLoginAttemptedEvent(
  loginMethod: EventConstructorArgs<
    'UserLoginAttempted',
    '1-1-1'
  >['payload']['loginMethod'],
  platformType: 'in_app_browser' | 'mobile_app' | 'mobile_web' | 'desktop_web'
) {
  const event = new UserLoginAttempted({
    version: '1-1-1',
    payload: {
      loginMethod,
      platformType,
    },
  });
  publishEvent(event);
}

export function publishAuthIdentifierSubmittedEvent(
  authFlow: AuthFlow,
  identifierType: AuthIdentifierType
) {
  const event = new AuthIdentifierSubmitted({
    version: '1-0-0',
    payload: {
      authFlow,
      identifierType,
    },
  });
  publishEvent(event);
}

export function publishAuthPasswordPromptDisplayedEvent() {
  const event = new AuthPasswordPromptDisplayed({
    version: '1-0-0',
    payload: {
      authFlow: 'login',
    },
  });
  publishEvent(event);
}

export function publishAuthPasswordSubmittedEvent() {
  const event = new AuthPasswordSubmitted({
    version: '1-0-0',
    payload: {
      authFlow: 'login',
    },
  });
  publishEvent(event);
}

export function publishAuthPasswordVerificationFailedEvent(
  failureReasonCode: AuthPasswordVerificationFailureReasonCode
) {
  const event = new AuthPasswordVerificationFailed({
    version: '1-0-0',
    payload: {
      authFlow: 'login',
      failureReasonCode,
    },
  });
  publishEvent(event);
}

export function publishAuthProtectedIdentifierVerificationSubmittedEvent() {
  const event = new AuthProtectedIdentifierVerificationSubmitted({
    version: '1-0-0',
    payload: {
      authFlow: 'login',
      identifierType: 'username',
      verificationType: 'email_confirmation',
    },
  });
  publishEvent(event);
}

export function publishAuthProtectedIdentifierVerificationCompletedEvent(
  requestOutcome: EventConstructorArgs<
    'AuthProtectedIdentifierVerificationCompleted',
    '1-0-0'
  >['payload']['requestOutcome'],
  failureReasonCode?: AuthProtectedIdentifierVerificationFailureReasonCode
) {
  const event = new AuthProtectedIdentifierVerificationCompleted({
    version: '1-0-0',
    payload: {
      authFlow: 'login',
      identifierType: 'username',
      verificationType: 'email_confirmation',
      requestOutcome,
      ...(failureReasonCode ? { failureReasonCode } : {}),
    },
  });
  publishEvent(event);
}

export function publishAuthOtpChallengeRequestedEvent(
  authFlow: AuthFlow,
  requestTrigger: AuthOtpRequestTrigger,
  otpChannel: AuthOtpChannel = OTP_CHANNEL_EMAIL
) {
  const event = new AuthOtpChallengeRequested({
    version: '1-0-0',
    payload: {
      authFlow,
      otpChannel,
      requestTrigger,
    },
  });
  publishEvent(event);
}

export function publishAuthOtpChallengeRequestCompletedEvent(
  authFlow: AuthFlow,
  requestTrigger: AuthOtpRequestTrigger,
  requestOutcome: EventConstructorArgs<
    'AuthOtpChallengeRequestCompleted',
    '2-0-0'
  >['payload']['requestOutcome'],
  failureReasonCode?: AuthOtpChallengeRequestFailureReasonCode,
  otpChannel: AuthOtpChannel = OTP_CHANNEL_EMAIL
) {
  const event = new AuthOtpChallengeRequestCompleted({
    version: '2-0-0',
    payload: {
      authFlow,
      otpChannel,
      requestTrigger,
      requestOutcome,
      ...(failureReasonCode ? { failureReasonCode } : {}),
    },
  });
  publishEvent(event);
}

export function publishAuthOtpVerificationSubmittedEvent(
  authFlow: AuthFlow,
  otpChannel: AuthOtpChannel = OTP_CHANNEL_EMAIL
) {
  const event = new AuthOtpVerificationSubmitted({
    version: '1-0-0',
    payload: {
      authFlow,
      otpChannel,
    },
  });
  publishEvent(event);
}

export function publishAuthOtpVerificationFailedEvent(
  authFlow: AuthFlow,
  failureReasonCode: AuthOtpVerificationFailureReasonCode,
  otpChannel: AuthOtpChannel = OTP_CHANNEL_EMAIL
) {
  const event = new AuthOtpVerificationFailed({
    version: '1-0-0',
    payload: {
      authFlow,
      otpChannel,
      failureReasonCode,
    },
  });
  publishEvent(event);
}

export function publishAuthMfaChallengeRequestedEvent(
  authFlow: AuthFlow,
  requestTrigger: AuthMfaRequestTrigger,
  mfaType: AuthMfaType,
  mfaFlow: AuthMfaFlow
) {
  const event = new AuthMfaChallengeRequested({
    version: '1-0-0',
    payload: {
      authFlow,
      mfaType,
      mfaFlow,
      requestTrigger,
    },
  });
  publishEvent(event);
}

export function publishAuthMfaChallengeRequestCompletedEvent(
  authFlow: AuthFlow,
  requestTrigger: AuthMfaRequestTrigger,
  requestOutcome: EventConstructorArgs<
    'AuthMfaChallengeRequestCompleted',
    '1-0-0'
  >['payload']['requestOutcome'],
  mfaType: AuthMfaType,
  mfaFlow: AuthMfaFlow,
  failureReasonCode?: AuthMfaChallengeRequestFailureReasonCode
) {
  const event = new AuthMfaChallengeRequestCompleted({
    version: '1-0-0',
    payload: {
      authFlow,
      mfaType,
      mfaFlow,
      requestTrigger,
      requestOutcome,
      ...(failureReasonCode ? { failureReasonCode } : {}),
    },
  });
  publishEvent(event);
}

export function publishAuthMfaVerificationSubmittedEvent(
  authFlow: AuthFlow,
  mfaType: AuthMfaType,
  mfaFlow: AuthMfaFlow
) {
  const event = new AuthMfaVerificationSubmitted({
    version: '1-0-0',
    payload: {
      authFlow,
      mfaType,
      mfaFlow,
    },
  });
  publishEvent(event);
}

export function publishAuthMfaVerificationFailedEvent(
  authFlow: AuthFlow,
  failureReasonCode: AuthMfaVerificationFailureReasonCode,
  mfaType: AuthMfaType,
  mfaFlow: AuthMfaFlow
) {
  const event = new AuthMfaVerificationFailed({
    version: '1-0-0',
    payload: {
      authFlow,
      mfaType,
      mfaFlow,
      failureReasonCode,
    },
  });
  publishEvent(event);
}

export const publishButtonClickedEvent = (
  componentName: string,
  screenTitle: GenericComponentScreenTitle
) => {
  const event = new GenericComponentInteracted({
    version: '1-0-0',
    payload: {
      componentName,
      componentType: 'Button',
      action: 'Clicked',
      screen: {
        screenTitle,
      },
    },
  });
  publishEvent(event);
};

export const publishGoogleOneTapComponentViewedEvent = (
  screenTitle: EventConstructorArgs<
    'GenericComponentInteracted',
    '1-0-0'
  >['payload']['screen']['screenTitle']
) => {
  const event = new GenericComponentInteracted({
    version: '1-0-0',
    payload: {
      componentName: 'Google One Tap',
      componentType: 'Modal',
      action: 'Viewed',
      screen: {
        screenTitle,
      },
    },
  });
  publishEvent(event);
};

const isBrowser = () => typeof window !== 'undefined';

export const createPageViewedEvent = () => {
  if (!isBrowser() || isMobileApp()) {
    return undefined;
  }

  /**
   * TODO: Update the models that use this event to use the new golden ScreenViewed event instead,
   * using a golden event should also help with collect the device type using event envelope
   * Before doing that, we need to work with Tristan to make sure attribution models are not affected
   */
  return new PageViewed({
    version: '1-1-1',
    payload: {
      browserId: getBrowserId(),
      eventType: 'PageViewed',
      context: {
        url: window.location.href,
        language:
          navigator.languages && navigator.languages[0]
            ? navigator.languages[0]
            : navigator.language
              ? navigator.language
              : 'en',
        userAgent: navigator.userAgent,
        referrer: document.referrer,
        channel: 'web',
        device: '',
      },
    },
  });
};

export const publishPageViewedEvent = () => {
  try {
    const event = createPageViewedEvent();

    if (event) {
      void publishNonGoldenEvent(event);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.info('analytics:pushInitialEvents failed', error);
  }
};
